import {
  SecurityTypesUtils,
  SettleValueTypeEnum,
  SideEnum,
  allowContracts,
  bigMax,
  isCross,
  isDelta1Multileg,
  isDeribitLinearOption,
  isOptionStrategy,
  isSecurityQuantoFuture,
  logger,
  toBigWithDefault,
  useSecuritiesContext,
  useStrategiesContext,
  type OrderFormSides,
  type OrderStrategy,
  type RequiredProperties,
  type Security,
} from '@talos/kyoko';
import { compact, uniq } from 'lodash-es';
import { useCallback } from 'react';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useTradingSettings } from '../../providers/TradingSettingsContext';
import { STRATEGY_GROUP } from '../../tokens/order';
import { assertSecurityOnlyPlaceInQtyContract, getLegSecurities, isDelta1NonSpotLegs } from '../../utils/security';
import { getCrossCurrency } from './utils';

interface GetOrderCurrencyParams {
  security: Security | undefined;
  securitiesBySymbol: Map<string, Security>;
  strategiesByName: Map<string, OrderStrategy>;
  enableDerivativeContractDefault: boolean;
  nonSpotDelta1DefaultQty?: string;
  orderSide?: SideEnum | OrderFormSides;
  strategy?: string;
  currentOrderCurrency?: string;
}

interface GetAllowedCurrenciesParams {
  security: Security | undefined;
  securitiesBySymbol: Map<string, Security>;
  strategiesByName: Map<string, OrderStrategy>;
  orderSide: SideEnum | undefined;
  strategy: string;
  disableCrossCurrencyQty: boolean;
}

/**
 * A hook to get correct order currency and all available order currencies
 * for specified security, order side and strategy.
 *
 * Note: The getOrderCurrency function should return the currency the backend is streaming MarketData in. NOT the
 * a currency the user is manually selecting to place the order in.
 */
export function useGetOrderCurrency(): {
  getOrderCurrency: (
    security: Security | undefined,
    orderSide?: SideEnum | OrderFormSides,
    strategy?: string,
    currentOrderCurrency?: string
  ) => string;
  getAllowedCurrencies: (security: Security | undefined, orderSide: SideEnum | undefined, strategy: string) => string[];
} {
  const { strategiesByName } = useStrategiesContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { enableDerivativeContractDefault } = useTradingSettings();
  const { disableCrossCurrencyQty } = useFeatureFlag();

  /*
   * Get order currency based on security, order side and strategy.
   *
   * @param security Security
   * @param orderSide the side of the order
   * @param strategy the strategy of the order
   * @param currentOrderCurrency current order currency
   */
  const getOrderCurrencyFn = useCallback(
    (
      security: Security | undefined,
      orderSide?: SideEnum | OrderFormSides,
      strategy?: string,
      currentOrderCurrency?: string
    ) => {
      return getOrderCurrency({
        security,
        securitiesBySymbol,
        strategiesByName,
        enableDerivativeContractDefault,
        orderSide,
        strategy,
        currentOrderCurrency,
      });
    },
    [securitiesBySymbol, strategiesByName, enableDerivativeContractDefault]
  );

  /*
   * Get allowed order currencies based on security, order side and strategy.
   *
   * @param security Security
   * @param orderSide the side of the order
   * @param strategy the strategy of the order
   */
  const getAllowedCurrenciesFn = useCallback(
    (security: Security | undefined, orderSide: SideEnum | undefined, strategy: string): string[] => {
      return getAllowedCurrencies({
        security,
        securitiesBySymbol,
        strategiesByName,
        orderSide,
        strategy,
        disableCrossCurrencyQty,
      });
    },
    [securitiesBySymbol, strategiesByName, disableCrossCurrencyQty]
  );

  return {
    getOrderCurrency: getOrderCurrencyFn,
    getAllowedCurrencies: getAllowedCurrenciesFn,
  };
}

export function getOrderCurrency({
  security,
  securitiesBySymbol,
  strategiesByName,
  enableDerivativeContractDefault,
  nonSpotDelta1DefaultQty,
  orderSide,
  strategy,
  currentOrderCurrency,
}: GetOrderCurrencyParams): string {
  if (!security) {
    return '';
  }

  if (SecurityTypesUtils.PredictionOutcome.isProductType(security)) {
    if (!currentOrderCurrency && enableDerivativeContractDefault) {
      return '';
    }
    return currentOrderCurrency ?? security.QuoteCurrency ?? '';
  }

  // https://talostrading.atlassian.net/browse/UI-3375
  if (isDelta1Multileg(security)) {
    const currencies = getDelta1AllowedCurrencies(security, securitiesBySymbol);
    if (nonSpotDelta1DefaultQty === 'Contract' && currencies.includes('')) {
      return '';
    }
    // Kinda hacky. If we are not defaulting to contracts, return the first valid currency
    if (nonSpotDelta1DefaultQty === 'Base' && currencies.some(currency => currency !== '')) {
      return currencies.find(currency => currency !== '') ?? currencies[0];
    }
    return currencies[0];
  }

  if (isOptionStrategy(security)) {
    return security.BaseCurrency;
  }

  if (isSecurityQuantoFuture(security) || assertSecurityOnlyPlaceInQtyContract(security)) {
    return ''; // meaning contracts
  }

  if (!currentOrderCurrency && allowContracts(security) && enableDerivativeContractDefault) {
    return '';
  }

  if (!security.BaseCurrency) {
    // This is not trying to convey contracts, it is conveying "not supported". The order form does not support BaseCurrency = undefined (compound instruments)
    // Returning "" in this if statement does not mean contracts, it means unsupported. The code assumes that the contracts and unsupported codepaths will not converge in this case.
    // It is terrible how we are modeling both failure cases and contracts as "" in this code path. Has to be fixed soon (todo Olof).
    logger.error(new Error('getOrderCurrency called with undefined security.BaseCurrency'), {
      extra: {
        security,
        strategy,
        currentOrderCurrency,
      },
    });
    return '';
  }

  const selectedStrategy = strategy ? strategiesByName.get(strategy) : undefined;
  if (selectedStrategy && selectedStrategy.Group === STRATEGY_GROUP.DARK) {
    // UI-2626
    return orderSide === SideEnum.Buy ? security.QuoteCurrency : security.BaseCurrency;
  } else {
    if (currentOrderCurrency != null) {
      // do not change currency if we're here and currency is already selected
      return currentOrderCurrency;
    }
    return security?.SettleValueType === SettleValueTypeEnum.Inverted ? security.QuoteCurrency : security.BaseCurrency;
  }
}

export function getAllowedCurrencies({
  security,
  securitiesBySymbol,
  strategiesByName,
  orderSide,
  strategy,
  disableCrossCurrencyQty,
}: GetAllowedCurrenciesParams): string[] {
  if (!security) {
    return [];
  }

  if (SecurityTypesUtils.PredictionOutcome.isProductType(security)) {
    return security.QuoteCurrency ? ['', security.QuoteCurrency] : [''];
  }

  if (!security.BaseCurrency) {
    // Security exists, but there is no BaseCurrency. The order form does not support this, shouldn't happen.
    logger.error(
      new Error('getAllowedCurrencies called with undefined security.BaseCurrency. This is not supported.'),
      {
        extra: {
          security,
          strategy,
        },
      }
    );
    return [];
  }

  if (isDeribitLinearOption(security)) {
    // https://talostrading.atlassian.net/browse/UI-4388
    return [security.BaseCurrency, ''];
  }

  if (isDelta1Multileg(security)) {
    return getDelta1AllowedCurrencies(security, securitiesBySymbol);
  }

  if (isOptionStrategy(security)) {
    return [security.BaseCurrency];
  }

  if (isCross(security)) {
    const { leg1, leg2 } = getLegSecurities(security, securitiesBySymbol);

    if (!leg1 || !leg2 || !leg1.BaseCurrency || !leg2.BaseCurrency) {
      return [];
    }

    const result = uniq([leg1.BaseCurrency, leg2.BaseCurrency, leg1.QuoteCurrency, leg2.QuoteCurrency]);

    if (disableCrossCurrencyQty) {
      const crossCurrency = getCrossCurrency(security, securitiesBySymbol);
      return result.filter(currency => currency !== crossCurrency);
    } else {
      return result;
    }
  }

  if (assertSecurityOnlyPlaceInQtyContract(security)) {
    return [''];
  }
  const result: string[] = [];
  const selectedStrategy = strategiesByName.get(strategy);
  if (selectedStrategy && selectedStrategy.Group === STRATEGY_GROUP.DARK) {
    // UI-2626
    if (orderSide === SideEnum.Buy) {
      result.push(security.QuoteCurrency);
    } else {
      result.push(security.BaseCurrency);
    }
  } else {
    result.push(security.BaseCurrency);
    result.push(security.QuoteCurrency);
  }
  if (allowContracts(security)) {
    result.push('');
  }
  return uniq(result);
}

const getDelta1AllowedCurrencies = (
  security: RequiredProperties<Security, 'MultilegDetails'>,
  securitiesBySymbol: Map<string, Security>
): string[] => {
  const { leg1, leg2 } = getLegSecurities(security, securitiesBySymbol);

  if (!leg1 || !leg2) {
    return [''];
  }

  // special case for bitmex:XBTUSD
  const isInvertedWithPositionCurrency =
    leg1.PositionCurrency && security.SettleValueType === SettleValueTypeEnum.Inverted;
  const baseOrQuoteCurrency = isInvertedWithPositionCurrency ? leg1.QuoteCurrency : leg1.BaseCurrency;

  if (isDelta1NonSpotLegs(security, securitiesBySymbol)) {
    if (!baseOrQuoteCurrency) {
      // Just return contracts ("") if there is no base or quote ccy
      return [''];
    } else {
      // first in array means default choice in NewOrderForm
      return ['', baseOrQuoteCurrency];
    }
  }

  return compact([baseOrQuoteCurrency]);
};

/**
 * Normalizes order quantity based on security, order currency, and other parameters.
 *
 * @param params - Parameters for normalizing order quantity
 * @returns Normalized order quantity as a string
 */
export function getNormalizedOrderQty(params: {
  security: Security | undefined;
  orderCurrency: string | undefined;
  size?: string;
  nonSpotDelta1DefaultQty?: string;
  securitiesBySymbol?: Map<string, Security>;
  strategiesByName?: Map<string, OrderStrategy>;
  disableCrossCurrencyQty?: boolean;
}): string {
  const { security, orderCurrency, size, securitiesBySymbol, strategiesByName, disableCrossCurrencyQty } = params;

  if (!orderCurrency) {
    // number of Contracts
    // For some security the NormalSize is less than MinimumSize; in these case use MinimumSize
    const initialSize = bigMax(security?.MinimumSize, security?.NormalSize, '1');
    return size || initialSize?.toFixed() || '';
  }

  // UI-7040: New approach - validate currency using the same logic as OrderSlice
  // Only validate if we have all the required context, otherwise fall back to existing behavior
  if (security && securitiesBySymbol && strategiesByName) {
    const allowedCurrencies = getAllowedCurrencies({
      security,
      securitiesBySymbol,
      strategiesByName,
      orderSide: undefined, // We don't have side context, but most validations don't depend on it
      strategy: '', // Default strategy for validation
      disableCrossCurrencyQty: disableCrossCurrencyQty ?? true, // Default to true as per feature flag default
    });

    // Check if the orderCurrency is in the list of allowed currencies
    if (!allowedCurrencies.includes(orderCurrency)) {
      // Currency is not valid for this security, fall back to default size
      return security?.NormalSize || '1';
    }
  }

  if (isDelta1NonSpotLegs(security, securitiesBySymbol)) {
    return size || security?.NormalSize || '1';
  }

  return toBigWithDefault(security?.NotionalMultiplier, 1)
    .times(size || security?.NormalSize || 1)
    .toFixed();
}
